import React from 'react';
import { formatEuro, type Board, type Reservation } from './types';
import {
    BoardColumn, getColumnValue, getColumnNumber, reservationEncaisse, reservationReste, totalledColumns,
} from './boardColumns';
import { OriginDot } from './ReservationSourcesPanel';

/**
 * La grille du tableau ouvert (maquette 16b).
 *
 * Les colonnes rendues suivent `board.columns.filter(visible)`, dans leur ordre — aucune colonne n'est
 * codée ici. La largeur du conteneur est en `max-content` plutôt qu'un `min-width` en pixels : une
 * valeur figée se désynchroniserait dès qu'on ajoute ou masque une colonne.
 *
 * Les deux premières colonnes (origine, client) sont figées en `sticky` avec un fond opaque, sinon les
 * cellules défilent dessous.
 */

const ORIGIN_W = 30;
const CLIENT_W = 186;

const widthFor = (col: BoardColumn) => {
    // Un commentaire tient rarement en 120 px : on lui en donne le double, et la cellule porte le texte
    // entier en infobulle pour ce qui dépasse encore.
    if (col.id === 'comment') return 230;
    if (col.type === 'montant') return 92;
    if (col.type === 'nombre') return 62;
    if (col.type === 'heure') return 74;
    if (col.type === 'personne') return 118;
    if (col.type === 'choix') return 104;
    return 120;
};

const alignFor = (col: BoardColumn) =>
    col.type === 'montant' ? 'justify-end text-right'
        : col.type === 'nombre' || col.type === 'heure' ? 'justify-center text-center'
            : 'justify-start text-left';

export function BoardGrid({ board, reservations, renderRowActions, actionsWidth = 96 }: {
    board: Board;
    reservations: Reservation[];
    /** Actions de fin de ligne (alerte e-mail, modifier, supprimer) — fournies par l'écran parent. */
    renderRowActions?: (r: Reservation) => React.ReactNode;
    actionsWidth?: number;
}) {
    const [sortKey, setSortKey] = React.useState<string | null>(null);
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');
    const readOnly = !renderRowActions;
    const columns = board.columns.filter((c) => c.visible && c.id !== 'client');
    const totalled = totalledColumns(board.columns).filter((c) => c.id !== 'client');

    // Le tri suit les colonnes affichées : une colonne ajoutée par l'utilisateur est triable comme les
    // autres, sans table de correspondance à tenir à jour.
    const sorted = React.useMemo(() => {
        if (!sortKey) return reservations;
        const col = board.columns.find((c) => c.id === sortKey);
        if (!col) return reservations;
        const numeric = col.type === 'montant' || col.type === 'nombre';
        return [...reservations].sort((a, b) => {
            if (numeric) {
                const d = getColumnNumber(a, col) - getColumnNumber(b, col);
                return sortDir === 'asc' ? d : -d;
            }
            const sa = String(getColumnValue(a, col) ?? '').toLowerCase();
            const sb = String(getColumnValue(b, col) ?? '').toLowerCase();
            return sortDir === 'asc' ? sa.localeCompare(sb, 'fr') : sb.localeCompare(sa, 'fr');
        });
    }, [reservations, sortKey, sortDir, board.columns]);

    const toggleSort = (id: string) => {
        if (sortKey === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortKey(id); setSortDir('asc'); }
    };

    // Une colonne de grille par colonne visible, plus le reste dû et les actions.
    const template = [
        `${ORIGIN_W}px`, `${CLIENT_W}px`,
        ...columns.map((c) => `${widthFor(c)}px`),
        '92px',
        readOnly ? '' : `${actionsWidth}px`,
    ].filter(Boolean).join(' ');

    const dateLabel = board.serviceDate
        ? new Date(`${board.serviceDate}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
        : '';

    const totalsFor = (col: BoardColumn) =>
        reservations.reduce((sum, r) => sum + getColumnNumber(r, col), 0);

    const totalReste = reservations.reduce((sum, r) => sum + reservationReste(r, board.columns), 0);

    return (
        <div className="overflow-x-auto bg-surface">
            <div className="w-max min-w-full">
                {/* En-tête */}
                <div className="grid bg-surface-soft border-b border-border" style={{ gridTemplateColumns: template }}>
                    <div className="py-2.5 sticky left-0 bg-surface-soft text-center text-[11px] font-bold text-content-muted">·</div>
                    <div className="py-2.5 px-3 sticky bg-surface-soft border-r border-border-soft text-[9.5px] font-bold uppercase tracking-[.07em] text-content-muted whitespace-nowrap"
                        style={{ left: ORIGIN_W }}>
                        Client
                    </div>
                    {columns.map((col) => (
                        <button key={col.id} type="button" onClick={() => toggleSort(col.id)}
                            className={`py-2.5 px-2 text-[9.5px] font-bold uppercase tracking-[.07em] whitespace-nowrap hover:text-content transition-colors ${
                                sortKey === col.id ? 'text-content' : 'text-content-muted'
                            } ${col.type === 'montant' ? 'text-right' : col.type === 'nombre' || col.type === 'heure' ? 'text-center' : 'text-left'}`}>
                            {col.label}{sortKey === col.id && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                        </button>
                    ))}
                    <div className="py-2.5 px-2 text-right text-[9.5px] font-bold uppercase tracking-[.07em] text-content-muted whitespace-nowrap">Reste</div>
                    {!readOnly && <div className="py-2.5 px-2" />}
                </div>

                {/* Lignes */}
                {sorted.map((r) => (
                    <div key={r.id} className="grid items-stretch border-b border-border-soft bg-surface hover:bg-surface-soft transition-colors group"
                        style={{ gridTemplateColumns: template }}>
                        <div className="sticky left-0 bg-inherit flex items-center justify-center">
                            <OriginDot source={r.source} />
                        </div>
                        <div className="py-2 px-3 sticky bg-inherit border-r border-border-soft min-w-0" style={{ left: ORIGIN_W }}>
                            <div className="flex items-center gap-1.5 min-w-0">
                                <span className="text-[12.5px] font-bold text-content truncate">
                                    {`${r.lastName} ${r.firstName}`.trim() || '—'}
                                </span>
                                {r.tableNumber && (
                                    <span className="inline-flex items-center h-[18px] px-1.5 rounded-[5px] bg-surface-soft text-content text-[9.5px] font-bold tabular-nums shrink-0">
                                        T{r.tableNumber}
                                    </span>
                                )}
                            </div>
                            <div className="text-[10.5px] text-content-muted tabular-nums truncate">{r.phone}</div>
                        </div>

                        {columns.map((col) => (
                            <Cell key={col.id} column={col} reservation={r} />
                        ))}

                        <div className="py-2 px-2 flex items-center justify-end text-xs font-bold tabular-nums whitespace-nowrap"
                            style={{ color: reservationReste(r, board.columns) > 0 ? 'var(--st-warn-fg)' : undefined }}>
                            {formatEuro(reservationReste(r, board.columns))}
                        </div>

                        {renderRowActions && (
                            <div className="py-2 px-2 flex items-center justify-end gap-0.5">
                                {renderRowActions(r)}
                            </div>
                        )}
                    </div>
                ))}

                {/* Totaux — fond sombre quel que soit le thème, comme la maquette : c'est la ligne qu'on
                    cherche des yeux en fin de service. */}
                <div className="grid bg-ink text-white" style={{ gridTemplateColumns: template }}>
                    <div className="sticky left-0 bg-ink" />
                    <div className="py-3 px-3 sticky bg-ink text-[10px] font-bold uppercase tracking-[.08em] text-white/70 whitespace-nowrap"
                        style={{ left: ORIGIN_W }}>
                        Totaux{dateLabel ? ` du ${dateLabel}` : ''}
                    </div>
                    {columns.map((col) => {
                        const isTotalled = totalled.some((t) => t.id === col.id);
                        return (
                            <div key={col.id} className={`py-3 px-2 text-[12.5px] font-bold tabular-nums whitespace-nowrap ${
                                col.type === 'montant' ? 'text-right' : 'text-center'
                            }`}>
                                {isTotalled
                                    ? (col.type === 'montant' ? formatEuro(totalsFor(col)) : totalsFor(col))
                                    : ''}
                            </div>
                        );
                    })}
                    <div className="py-3 px-2 text-right text-[12.5px] font-bold tabular-nums whitespace-nowrap" style={{ color: '#f0a37c' }}>
                        {formatEuro(totalReste)}
                    </div>
                    {!readOnly && <div className="py-3 px-2" />}
                </div>
            </div>
        </div>
    );
}

/** Une cellule, rendue selon le type de sa colonne. */
function Cell({ column, reservation }: { column: BoardColumn; reservation: Reservation }) {
    const align = alignFor(column);
    const raw = getColumnValue(reservation, column);

    if (column.type === 'montant') {
        const n = getColumnNumber(reservation, column);
        return (
            <div className={`py-2 px-2 flex items-center ${align} text-xs tabular-nums whitespace-nowrap ${n ? 'text-content font-semibold' : 'text-content-muted'}`}>
                {n ? formatEuro(n) : '—'}
            </div>
        );
    }

    if (column.type === 'nombre') {
        const n = getColumnNumber(reservation, column);
        return (
            <div className={`py-2 px-2 flex items-center ${align} text-xs tabular-nums ${n ? 'text-content' : 'text-content-muted'}`}>
                {n || '—'}
            </div>
        );
    }

    if (column.type === 'choix') {
        return (
            <div className="py-2 px-2.5 flex items-center min-w-0">
                {raw ? (
                    <span className="inline-flex items-center h-[21px] px-2.5 rounded-full bg-surface-soft border border-border text-content text-[10.5px] font-bold truncate">
                        {String(raw)}
                    </span>
                ) : null}
            </div>
        );
    }

    if (column.type === 'personne') {
        // Les deux noms empilés dans la même cellule : « pris par » au-dessus, « encaissé par » en dessous.
        return (
            <div className="py-2 px-2.5 flex flex-col justify-center gap-0.5 min-w-0">
                <span className="text-[11px] font-semibold text-content truncate">{reservation.takenBy || '—'}</span>
                <span className="text-[10.5px] text-content-muted truncate">{reservation.cashedBy || '—'}</span>
            </div>
        );
    }

    return (
        <div className={`py-2 px-2 flex items-center ${align} text-xs text-content truncate`}
            title={raw ? String(raw) : undefined}>
            {raw ? String(raw) : <span className="text-content-muted">—</span>}
        </div>
    );
}

/** Les quatre tuiles sous la grille (maquette 16b). */
export function BoardTotals({ board, reservations }: { board: Board; reservations: Reservation[] }) {
    let covers = 0, theorique = 0, encaisse = 0;
    reservations.forEach((r) => {
        covers += r.adults + r.children;
        theorique += r.adults * r.tarifAdult + r.children * r.tarifChild;
        encaisse += reservationEncaisse(r, board.columns);
    });
    const reste = Math.max(0, theorique - encaisse);

    return (
        <div className="px-4 py-3.5 bg-surface border-t border-border grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(186px,1fr))]">
            <Tile label="Couverts" value={String(covers)} />
            <Tile label="CA théorique" value={formatEuro(theorique)} />
            <Tile label="Encaissé" value={formatEuro(encaisse)} tone="ok" />
            <Tile label="Reste à payer" value={formatEuro(reste)} tone={reste > 0 ? 'warn' : 'neutral'} />
        </div>
    );
}

function Tile({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'ok' | 'warn' | 'neutral' }) {
    const tones = {
        ok: 'bg-state-ok-soft border-state-ok/25 text-state-ok',
        warn: 'bg-state-warn-soft border-state-warn/25 text-state-warn',
        neutral: 'bg-surface-soft border-border-soft text-content',
    } as const;
    return (
        <div className={`px-3.5 py-3 rounded-[11px] border ${tones[tone]}`}>
            <div className="text-[9.5px] font-bold uppercase tracking-[.07em] opacity-80 mb-1">{label}</div>
            <div className="text-xl font-bold tabular-nums whitespace-nowrap">{value}</div>
        </div>
    );
}
